import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

import type { CombinedFilterType, FilterName } from '@client/hooks/useAdjustedQuotes';
import { formatCurrency } from '@client/lib';
import { UNDERWRITING_STATUS_CONFIG } from '@shared';

interface AdjustedQuotesBannerProps {
  premiumValue: number;
  appliedFilters: FilterName[];
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: CombinedFilterType;
  selectedEligibilityFilters?: string[];
  onRemoveEligibilityFilter?: (filter: string) => void;
  selectedFeatureBenefitsFilters?: string[];
  onRemoveFeatureBenefitsFilter?: (filter: string) => void;
}

export function AdjustedQuotesBanner({
  premiumValue,
  appliedFilters,
  benefitDurationValue,
  inflationProtectionValue,
  selectedEligibilityFilters = [],
  onRemoveEligibilityFilter,
  selectedFeatureBenefitsFilters = [],
  onRemoveFeatureBenefitsFilter,
}: AdjustedQuotesBannerProps) {
  function getFilterLabel(filter: FilterName) {
    switch (filter) {
      case 'premium':
        return `${formatCurrency(premiumValue)} Premium`;
      case 'inflationProtection':
        if (inflationProtectionValue.type === 'combined') {
          return `${inflationProtectionValue.combined[0]}%-${inflationProtectionValue.combined[1]}% Inflation`;
        } else {
          return `AOB: ${inflationProtectionValue.aob[0]}%-${inflationProtectionValue.aob[1]}%, COB: ${inflationProtectionValue.cob[0]}%-${inflationProtectionValue.cob[1]}% Inflation`;
        }
      case 'benefitDuration':
        if (benefitDurationValue.type === 'combined') {
          return `${benefitDurationValue.combined[0]}-${benefitDurationValue.combined[1]} Years Duration`;
        } else {
          return `AOB: ${benefitDurationValue.aob[0]}-${benefitDurationValue.aob[1]}, COB: ${benefitDurationValue.cob[0]}-${benefitDurationValue.cob[1]} Years Duration`;
        }
      default:
        return '';
    }
  }

  function getFeatureBenefitsLabel(filter: string) {
    switch (filter) {
      case 'hasGlobalBenefits':
        return 'International Benefits';
      default:
        return filter;
    }
  }

  const filterOrder: FilterName[] = ['premium', 'inflationProtection', 'benefitDuration'];

  const sortedFilters = appliedFilters.sort((a, b) => {
    return filterOrder.indexOf(a) - filterOrder.indexOf(b);
  });

  return (
    <div className="mb-4 flex items-start gap-2 py-2">
      <p className="shrink-0 text-sm font-medium text-gray-600">Active Filters</p>
      <div className="flex flex-wrap gap-2">
        {/* Regular filters */}
        {sortedFilters.map(filter => (
          <Badge
            key={filter}
            variant="outline"
            className="border-primary text-primary-dark rounded-sm px-3 py-1 text-sm"
          >
            {getFilterLabel(filter)}
          </Badge>
        ))}
        {/* Eligibility filters */}
        {selectedEligibilityFilters.map(filter => {
          const config =
            UNDERWRITING_STATUS_CONFIG[filter as keyof typeof UNDERWRITING_STATUS_CONFIG];
          const filterLabel = config?.label || filter;

          return (
            <Badge
              key={filter}
              variant="outline"
              className="border-primary text-primary-dark rounded-sm px-3 py-1 text-sm"
            >
              {filterLabel}
              {onRemoveEligibilityFilter && (
                <button
                  onClick={() => onRemoveEligibilityFilter(filter)}
                  className="text-primary ml-2 inline-flex hover:opacity-70"
                  aria-label={`Remove ${filterLabel} filter`}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </Badge>
          );
        })}
        {/* Feature Benefits filters */}
        {selectedFeatureBenefitsFilters.map(filter => {
          const filterLabel = getFeatureBenefitsLabel(filter);
          return (
            <Badge
              key={filter}
              variant="outline"
              className="border-primary text-primary-dark rounded-sm px-3 py-1 text-sm"
            >
              {filterLabel}
              {onRemoveFeatureBenefitsFilter && (
                <button
                  onClick={() => onRemoveFeatureBenefitsFilter(filter)}
                  className="text-primary ml-2 inline-flex hover:opacity-70"
                  aria-label={`Remove ${filterLabel} filter`}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
